import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import he from "he";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCheck, faClock, faQuestion, faStar, faXmark } from "@fortawesome/free-solid-svg-icons";
import { extractReportIdFromResponse, sendReport } from "../../functions/sendReport";
import { getStoredUser, getUserToken, themeCssVars } from "../../functions/themeAssets";
import "../common/player.css";
import "./game.css";

const DEMO_QUESTIONS = 1; // demo players only answer the first question
const MOBILE_MAX = 700; // under this width the swipe layout of questions_mo.php is used
const SWIPE_MIN = 90; // px, same threshold as the PHP swipe script
const SWIPE_MAX_MS = 800;
const OPTION_CLASSES = ["yes", "maybe", "no"];
const OPTION_ICONS = [faCheck, faQuestion, faXmark];
const OPTION_KEYS = { ArrowLeft: [0, "left"], ArrowUp: [1, "up"], ArrowRight: [2, "right"] };
const SAVE_EVERY_MS = 10000; // the timer is saved this often, so a refresh resumes close to where it was

const pad = (n) => (n > 9 ? String(n) : `0${n}`);
const formatClock = (total) => `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
const formatClockHms = (total) =>
  `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;

const decode = (s) => he.decode(String(s ?? ""));

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < MOBILE_MAX);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < MOBILE_MAX);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

function Hint({ q }) {
  if (!q.hintUrl) return null;
  if (q.hintType === "video") {
    return (
      <video key={q.hintUrl} className="show-image" controls>
        <source src={q.hintUrl} type="video/mp4" />
      </video>
    );
  }
  if (q.hintType === "audio") {
    return (
      <audio key={q.hintUrl} className="show-image" controls>
        <source src={q.hintUrl} type="audio/mpeg" />
      </audio>
    );
  }
  return <img src={q.hintUrl} className="show-image" alt="" />;
}

export default function GamePage() {
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const token = getUserToken(user);
  const isDemo = user?.role === "demobypass" || String(user?.sessionId || "").startsWith("demo");
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(null);
  const [questions, setQuestions] = useState([]);

  const [index, setIndex] = useState(0);
  const [points, setPoints] = useState(0);
  const [selected, setSelected] = useState(null); // 0 | 1 | 2 (Yes | Maybe | No)
  const [shown, setShown] = useState(0); // how many answer buttons have faded in
  const [flip, setFlip] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [leaving, setLeaving] = useState(null); // mobile: "left" | "up" | "right"
  const [entering, setEntering] = useState(false);
  const [finishing, setFinishing] = useState(false);

  const secondsRef = useRef(0);
  const timerRef = useRef(null);
  const timeoutsRef = useRef([]);
  const questionsRef = useRef([]);
  const indexRef = useRef(0);
  const pointsRef = useRef(0);
  const answersRef = useRef([]); // [{ id, answer: 1 | 2 | 3 }]
  const busyRef = useRef(false);
  const submittingRef = useRef(false);
  const touchRef = useRef(null);
  const finishRef = useRef(() => {});
  const reportIdRef = useRef(user?.reportId || null); // the player's report on the reports server
  const reportQueueRef = useRef(Promise.resolve()); // reports are sent one at a time, in order
  const reportNoIdRef = useRef(false); // "add" gave no id back: only the final score is sent

  const later = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timeoutsRef.current.push(t);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  };

  // answers so far + elapsed time, so a refresh continues this game (demo games are not resumed)
  const saveProgress = useCallback(
    (keepalive = false) => {
      if (isDemo || submittingRef.current || !questionsRef.current.length) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/saveGameProgress`, {
        method: "POST",
        keepalive,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          userId: user.userId,
          answers: answersRef.current.map((a) => a.answer),
          elapsed: secondsRef.current,
          reportId: reportIdRef.current,
        }),
      }).catch((err) => console.error("Progress save failed", err));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDemo, token]
  );

  /**
   * Score and time so far to the reports server. The first call adds the report and its id is kept
   * (in the progress row and the stored user), so every later call, even after a refresh, updates that report.
   */
  const pushReport = useCallback(
    (reportPoints, reportSeconds, { final = false } = {}) => {
      if (isDemo) return Promise.resolve();
      const run = async () => {
        if (!final && reportNoIdRef.current) return;
        try {
          const response = await sendReport({
            sessionId: user.sessionId,
            organizationId: user.organizationId,
            userId: user.userId,
            role: user.role || user.source,
            token: user.token,
            gameId: user.gameId,
            reportId: reportIdRef.current,
            name: user.name || "",
            points: reportPoints,
            time: formatClockHms(reportSeconds),
          });
          if (!reportIdRef.current) {
            const id = extractReportIdFromResponse(response);
            if (id) {
              reportIdRef.current = id;
              const stored = getStoredUser() || user;
              sessionStorage.setItem("userData", JSON.stringify({ ...stored, reportId: id }));
              saveProgress();
            } else {
              reportNoIdRef.current = true; // cannot update without an id; avoid a new report per question
              console.warn("Reports server returned no report id; only the final score will be sent");
            }
          }
        } catch (err) {
          console.error("Report push failed", err);
        }
      };
      reportQueueRef.current = reportQueueRef.current.then(run);
      return reportQueueRef.current;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDemo, saveProgress]
  );

  // ---------- load ----------
  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return undefined;
    }
    let cancelled = false;
    const timeouts = timeoutsRef.current;

    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getGameData`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            userId: user.userId,
            name: user.name || "",
            email: user.email || "",
            userType: user.role || user.source || "",
          }),
        });
        const json = await res.json();
        if (cancelled) return;

        if (json.played) {
          await Swal.fire({
            title: "Already Played",
            text: "You have already played this game.",
            icon: "info",
            confirmButtonText: "OK",
          });
          window.location.href = user.backButtonRedirect || process.env.REACT_APP_BASE_URL || "/";
          return;
        }
        if (!json.success) throw new Error(json.message || "Could not load the game");

        if (json.notEnoughQuestions) {
          await Swal.fire({
            title: "",
            text: "The number of questions shown is less than what’s set by the admin. The game will begin once the questions are updated.",
            icon: "warning",
            confirmButtonText: "OK",
          });
          navigate("/rules", { replace: true });
          return;
        }

        // the server picks this player's questions once and returns the same set on every refresh
        const picked = (json.questions || []).map((q) => ({
          id: q.id,
          text: decode(q.question_name),
          options: [decode(q.option_one) || "Yes", decode(q.option_two) || "Maybe", decode(q.option_three) || "No"],
          points: [q.option_point_one, q.option_point_two, q.option_point_three].map((p) => parseInt(p, 10) || 0),
          hintUrl: q.hintUrl,
          hintType: q.hintType,
        }));

        // resume: answers, points and time saved before the refresh
        const saved = json.progress;
        const done = saved ? saved.answers.slice(0, picked.length) : [];
        answersRef.current = done.map((answer, i) => ({ id: picked[i].id, answer }));
        pointsRef.current = saved ? Number(saved.points) || 0 : 0;
        secondsRef.current = saved ? Number(saved.elapsed) || 0 : 0;
        if (!reportIdRef.current && saved?.reportId) reportIdRef.current = saved.reportId;

        questionsRef.current = picked;
        setTheme(json.theme);
        setQuestions(picked);
        setPoints(pointsRef.current);
        setSeconds(secondsRef.current);

        const total = isDemo ? Math.min(DEMO_QUESTIONS, picked.length) : picked.length;
        if (done.length >= total) {
          // every question was answered but the score was not saved yet (refresh while submitting)
          indexRef.current = total - 1;
          setIndex(total - 1);
          setLoading(false);
          finishRef.current();
          return;
        }

        indexRef.current = done.length;
        setIndex(done.length);
        setLoading(false);
        timerRef.current = setInterval(() => {
          secondsRef.current += 1;
          setSeconds(secondsRef.current);
        }, 1000);
      } catch (err) {
        if (cancelled) return;
        await Swal.fire("Error", err.message || "Could not load the game", "error");
        navigate("/rules", { replace: true });
      }
    })();

    return () => {
      cancelled = true;
      stopTimer();
      timeouts.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = questions[index];

  // ---------- a new question: answers fade in one by one, the number badge flips ----------
  useEffect(() => {
    if (loading) return undefined;
    indexRef.current = index;
    setShown(0);
    setSelected(null);
    setLeaving(null);
    setEntering(true);
    setFlip(false);
    const frame = requestAnimationFrame(() => requestAnimationFrame(() => setEntering(false)));
    const timers = [
      setTimeout(() => setFlip(true), 50),
      setTimeout(() => setShown(1), 250),
      setTimeout(() => setShown(2), 500),
      setTimeout(() => {
        setShown(3);
        busyRef.current = false;
      }, 750),
    ];
    return () => {
      cancelAnimationFrame(frame);
      timers.forEach(clearTimeout);
    };
  }, [index, loading]);

  // ---------- finishing ----------
  const submitResult = useCallback(async () => {
    if (submittingRef.current) return null;
    submittingRef.current = true;
    const time = formatClock(secondsRef.current);

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/submitGame`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          userId: user.userId,
          name: user.name,
          email: user.email,
          role: user.role || user.source,
          time,
          storeMainID: answersRef.current.map((a) => a.id),
          storeAnswer: answersRef.current.map((a) => a.answer),
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "something went wrong");

      // external reports server (registered and guest players); the server's score is the official one
      if (!json.alreadySubmitted) {
        await pushReport(json.points ?? pointsRef.current, secondsRef.current, { final: true });
      }

      const stored = getStoredUser() || user;
      sessionStorage.setItem(
        "userData",
        JSON.stringify({ ...stored, gameover: 1, ...(reportIdRef.current && { reportId: reportIdRef.current }) })
      );
      return json;
    } catch (err) {
      submittingRef.current = false;
      return { failed: true, message: err.message };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, pushReport]);

  const finishGame = useCallback(async () => {
    stopTimer();
    setFinishing(true);
    let result = await submitResult();
    while (!result || result.failed) {
      const retry = await Swal.fire({
        title: "Something went wrong",
        text: result?.message || "Your score could not be saved.",
        icon: "error",
        confirmButtonText: "Try again",
        showCancelButton: true,
      });
      if (!retry.isConfirmed) {
        navigate("/rules", { replace: true });
        return;
      }
      result = await submitResult();
    }

    if (result.isdemo) {
      await Swal.fire({ title: "Subscribe to any PLAN to play with your peers.", icon: "success" });
      window.location.href = `${process.env.REACT_APP_BASE_URL || ""}/plans`;
      return;
    }
    later(() => navigate("/thankyou", { replace: true }), 800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitResult, navigate]);

  finishRef.current = finishGame;

  // keep the timer saved while playing, and once more when the page is refreshed or closed
  useEffect(() => {
    if (loading || isDemo) return undefined;
    const every = setInterval(() => saveProgress(), SAVE_EVERY_MS);
    const onLeave = () => saveProgress(true);
    const onVisibility = () => document.visibilityState === "hidden" && saveProgress(true);
    window.addEventListener("pagehide", onLeave);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(every);
      window.removeEventListener("pagehide", onLeave);
      document.removeEventListener("visibilitychange", onVisibility);
      saveProgress(true); // leaving the game screen inside the app
    };
  }, [loading, isDemo, saveProgress]);

  // ---------- one question answered ----------
  const commit = useCallback(
    (choice) => {
      const list = questionsRef.current;
      const q = list[indexRef.current];
      if (!q) return;

      answersRef.current.push({ id: q.id, answer: choice + 1 });
      pointsRef.current += q.points[choice];
      setPoints(pointsRef.current);
      setFlip(false);

      const total = isDemo ? Math.min(DEMO_QUESTIONS, list.length) : list.length;
      if (answersRef.current.length >= total) {
        finishGame();
      } else {
        saveProgress();
        pushReport(pointsRef.current, secondsRef.current);
        setIndex((i) => i + 1);
      }
    },
    [isDemo, finishGame, saveProgress, pushReport]
  );

  // desktop: pick an option, then SUBMIT
  const pick = (choice) => {
    if (busyRef.current || finishing) return;
    setSelected(choice);
  };

  const submit = () => {
    if (busyRef.current || finishing) return;
    if (selected === null) {
      Swal.fire("", "Please select an option", "error");
      return;
    }
    busyRef.current = true;
    commit(selected);
  };

  // mobile: swipe left = Yes, up = Maybe, right = No
  const swipe = useCallback(
    (choice, dir) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setSelected(choice);
      setLeaving(dir);
      later(() => commit(choice), 550);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commit]
  );

  const onTouchStart = (x, y) => {
    if (busyRef.current) return;
    touchRef.current = { x, y, at: Date.now() };
  };

  const onTouchEnd = (x, y) => {
    const start = touchRef.current;
    touchRef.current = null;
    if (!start || busyRef.current || Date.now() - start.at > SWIPE_MAX_MS) return;
    const dx = x - start.x;
    const dy = y - start.y;
    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx < -SWIPE_MIN) swipe(0, "left");
      else if (dx > SWIPE_MIN) swipe(2, "right");
    } else if (dy < -SWIPE_MIN) {
      swipe(1, "up");
    }
  };

  useEffect(() => {
    if (!isMobile || loading) return undefined;
    const onKey = (e) => {
      const hit = OPTION_KEYS[e.key];
      if (hit) swipe(hit[0], hit[1]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile, loading, swipe]);

  if (loading || !theme || !current) {
    return (
      <div className="iq-loader">
        <div className="iq-loader-spinner" />
      </div>
    );
  }

  const cssVars = themeCssVars(theme);
  const total = isDemo ? Math.min(DEMO_QUESTIONS, questions.length) : questions.length;
  const progress = (
    <div className="iq-progress">
      <div className="iq-progress-label">
        Question {index + 1} of {total}
      </div>
      <div className="iq-progress-track">
        <div className="iq-progress-fill" style={{ width: `${((index + 1) / total) * 100}%` }} />
      </div>
    </div>
  );
  const header = (
    <>
      <div className="iq-topbar">
        <div className="iq-pill iq-timer">
          <FontAwesomeIcon icon={faClock} className="iq-pill-icon" />
          Time: {formatClock(seconds)}
        </div>
        <div className="iq-pill iq-score">
          <FontAwesomeIcon icon={faStar} className="iq-pill-icon" />
          Points: {points}
        </div>
      </div>
      <h3 className="iq-title">{theme.landing_page_title}</h3>
    </>
  );
  const hint = current.hintUrl ? (
    <div className="iq-hint">
      <Hint q={current} />
    </div>
  ) : null;

  if (isMobile) {
    return (
      <div className="iq-game iq-m" style={cssVars}>
        {header}
        {progress}
        <div
          className="iq-stage"
          onTouchStart={(e) => onTouchStart(e.touches[0].clientX, e.touches[0].clientY)}
          onTouchEnd={(e) => onTouchEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY)}
          onMouseDown={(e) => onTouchStart(e.clientX, e.clientY)}
          onMouseUp={(e) => onTouchEnd(e.clientX, e.clientY)}
        >
          <div className={`iq-swipe-card${leaving ? ` leave-${leaving}` : ""}${entering ? " enter" : ""}`}>
            {current.text}
            {hint}
          </div>
          <div className={`iq-tab maybe${leaving === "up" ? " chosen" : ""}`}>
            <FontAwesomeIcon icon={faQuestion} /> {current.options[1]}
          </div>
          <div className={`iq-tab yes${leaving === "left" ? " chosen" : ""}`}>
            <FontAwesomeIcon icon={faCheck} /> {current.options[0]}
          </div>
          <div className={`iq-tab no${leaving === "right" ? " chosen" : ""}`}>
            <FontAwesomeIcon icon={faXmark} /> {current.options[2]}
          </div>
        </div>
        <div className="iq-swipe-info">
          Swipe left to answer yes, Swipe right to answer no and Swipe up for maybe
        </div>
      </div>
    );
  }

  return (
    <div className="iq-game" style={cssVars}>
      {header}
      <div className="iq-card-wrap">
        {progress}
        <div className="iq-card">
          <div className={`iq-qtext${flip ? " flip" : ""}`}>{current.text}</div>
          {hint}
          <div className="iq-answers">
            {current.options.map((label, i) => (
              <button
                type="button"
                key={i}
                className={`iq-answer ${OPTION_CLASSES[i]}${shown > i ? " on" : ""}${selected === i ? " picked" : ""}`}
                onClick={() => pick(i)}
                aria-pressed={selected === i}
              >
                <span className="iq-answer-icon">
                  <FontAwesomeIcon icon={OPTION_ICONS[i]} />
                </span>
                <span className="iq-answer-label">{label}</span>
              </button>
            ))}
          </div>
        </div>
        <button type="button" className="iq-submit" onClick={submit} disabled={finishing}>
          SUBMIT <FontAwesomeIcon icon={faArrowRight} />
        </button>
      </div>
    </div>
  );
}
